import { Sprite } from 'pixi.js';
import { Boss, type BossDef } from './boss-base';
import type { PatternRunner } from '@/patterns/emitter';
import { makeSprite } from '@/assets/resolver';
import type { PatternDef } from '@/patterns/dsl';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { SummonGate } from './summon-gate';

/**
 * B3 — Hive Mother. Minion-shield boss: while a fly wave is alive the hive
 * is wrapped in a shield and takes NO damage; clearing the wave opens a 5s
 * vulnerable window, then the next (bigger) wave hatches. First user of the
 * 'charge' signature movement. docs/02-bosses.md
 * Signatures: the spit (P1), the pulsing ring (P2), the panic ring.
 */
export const HIVE: BossDef = {
  id: 'hive',
  nameKey: 'boss.hive',
  tier: 2,
  baseHp: 850,
  radius: 86,
  bodyKey: 'boss/hive/body',
  movement: 'charge',
  contactDamage: 1,
  phases: [
    {
      // Phase 1: light aimed spit only — pressure comes from the flies
      hpPct: 1.0,
      patterns: [
        {
          // The sac swells before every spit (body cue, docs/02 B5)
          type: 'aimed', count: 1, spreadDeg: 0, interval: 2.6, telegraph: 0.4,
          bullet: 'bullet/enemy/spore', speed: 210, radius: 13, damage: 1,
        },
        {
          // Spore puff: a slow loose ring between spits
          type: 'radial', count: 8, interval: 3.6, spinDeg: 20, unlockTier: 2,
          bullet: 'bullet/enemy/ring', speed: 110, radius: 10, damage: 2,
        },
      ],
    },
    {
      // Phase 2 (50%): adds a pulsing ring + elite joins each wave
      hpPct: 0.5,
      speedMul: 1.25,
      patterns: [
        {
          type: 'radial', count: 10, interval: 3.0, spinDeg: 18,
          bullet: 'bullet/enemy/ring', speed: 125, radius: 10, damage: 2,
        },
        {
          type: 'aimed', count: 3, spreadDeg: 24, interval: 2.4,
          telegraph: 0.4, unlockTier: 1,
          bullet: 'bullet/enemy/spore', speed: 240, radius: 13, damage: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Royal brood: the swollen hive coughs a heavy six-way burst
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'radial', count: 6, interval: 2.4, spinDeg: 30,
        bullet: 'bullet/enemy/ring', speed: 145, radius: 11, damage: 2,
      },
    },
  ],
};

/**
 * Extra burst fired only during the vulnerable window (panic spray) — tier
 * gated and ramped like every other attack (X6).
 */
const VULNERABLE_PATTERNS: ReadonlyArray<PatternDef> = [
  {
    type: 'radial', count: 14, interval: 1.6, spinDeg: 25,
    bullet: 'bullet/enemy/spore', speed: 160, radius: 10, damage: 1,
  },
  {
    type: 'aimed', count: 3, spreadDeg: 20, interval: 1.9,
    telegraph: 0.4, unlockTier: 2,
    bullet: 'bullet/enemy/spore', speed: 250, radius: 10, damage: 1,
  },
];

const VULNERABLE_WINDOW = 5;
const BASE_WAVE = 4;
const WAVE_GROWTH = 2;
const MAX_WAVE = 12;

export class HiveBoss extends Boss {
  private readonly shield: Sprite;
  private waveNumber = 0;
  private vulnerableTimer = 0;
  /** Fly-wave shield — drops when the wave dies (or on the stall failsafe) */
  private readonly gate = new SummonGate();
  private vulnRunners: PatternRunner[] = [];
  private started = false;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(HIVE, level, x, y, homeX, homeY);
    // The hive already runs its own minion-shield cycle — no extra squad
    this.allowReinforce = false;
    this.shield = makeSprite('boss/hive/shield');
    this.shield.scale.set((HIVE.radius * 2.6) / 256);
    this.shield.visible = false;
    this.view.addChild(this.shield);
  }

  private get shielded(): boolean {
    return this.gate.locked;
  }

  get isShielded(): boolean {
    return this.shielded;
  }

  override get isVulnerable(): boolean {
    return super.isVulnerable && !this.shielded;
  }

  protected override get telegraphing(): boolean {
    return super.telegraphing || (!this.shielded && this.vulnRunners.some((r) => r.warning));
  }

  private hatchWave(): void {
    this.waveNumber++;
    const count = Math.min(MAX_WAVE, BASE_WAVE + (this.waveNumber - 1) * WAVE_GROWTH);
    this.spawnMinions?.('fly', count);
    if (this.phaseIndex >= 1) this.spawnMinions?.('elite', 1);
    this.gate.raise();
  }

  override update(
    dt: number,
    px: number,
    py: number,
    bullets: BulletSystem,
    telegraphs?: TelegraphSink,
  ): void {
    if (this.dead) return;

    if (!this.started) {
      this.started = true;
      this.hatchWave();
    }

    // Wave/shield state machine (needs game-wired minionCount)
    if (this.gate.update(dt, this.minionCount?.() ?? 0)) {
      this.vulnerableTimer = VULNERABLE_WINDOW;
      this.vulnRunners = this.makeRunners(VULNERABLE_PATTERNS);
    } else if (!this.shielded) {
      this.vulnerableTimer -= dt;
      for (const r of this.vulnRunners) {
        r.update(dt, this.x, this.y, px, py, bullets, telegraphs);
      }
      if (this.vulnerableTimer <= 0) this.hatchWave();
    }

    super.update(dt, px, py, bullets, telegraphs);
  }

  /** Shield blocks everything while the wave lives. */
  override takeDamage(amount: number): boolean {
    if (this.shielded) return false;
    return super.takeDamage(amount);
  }

  override render(alpha: number): void {
    super.render(alpha);
    this.shield.visible = this.shielded;
    if (this.shielded) {
      const pulse = 1 + Math.sin(performance.now() / 220) * 0.04;
      this.shield.scale.set(((HIVE.radius * 2.6) / 256) * pulse);
      this.shield.rotation += 0.004;
    }
  }
}
